import React, { useState, useRef } from 'react';
import { Camera, Loader2, Upload, X, CheckCircle2, FileVideo } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

const MAX_MB = 50;
const ALLOWED = ['video/mp4', 'video/quicktime', 'video/webm', 'video/mpeg', 'video/x-matroska'];

export default function VideoEvidence({ noticeId = null, onAnalysisComplete = () => {} }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null); setPreview(''); setErr(''); setAnalysis(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onPick = (f) => {
    setErr(''); setAnalysis(null);
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setErr('Use MP4, MOV, WEBM, MPEG, or MKV.');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setErr(`File too big. Limit is ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    onPick(f);
  };

  const submit = async () => {
    if (!file) return;
    setErr(''); setBusy(true); setAnalysis(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const fd = new FormData();
      fd.append('file', file);
      if (noticeId) fd.append('notice_id', noticeId);
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/video/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || `Upload failed (${resp.status})`);
      }
      const j = await resp.json();
      setAnalysis(j);
      onAnalysisComplete(j);
    } catch (e) {
      setErr(e.message || 'Video analysis failed');
    } finally {
      setBusy(false);
    }
  };

  const a = analysis?.analysis;

  return (
    <div className="tactical-card p-6" data-testid="video-evidence-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileVideo className="h-5 w-5 text-plai-purple" strokeWidth={1.5} />
          <h2 className="font-headings text-xl uppercase tracking-tight">Video Evidence</h2>
        </div>
        {file && !busy && (
          <button onClick={reset} className="text-white/50 hover:text-white" data-testid="video-clear-btn">
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        )}
      </div>

      {!file && (
        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="block border border-dashed border-white/20 p-6 text-center cursor-pointer hover:border-plai-teal transition-colors"
          data-testid="video-upload-label"
        >
          <input
            ref={inputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm,video/mpeg,video/x-matroska"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0])}
            data-testid="video-upload-input"
          />
          <Upload className="h-6 w-6 mx-auto text-white/40 mb-2" strokeWidth={1.5} />
          <p className="font-body text-xs text-white/70">Drop a 10-second clip</p>
          <p className="font-body text-[10px] text-white/40 mt-1">MP4 / MOV / WEBM. Up to {MAX_MB} MB.</p>
        </label>
      )}

      {file && (
        <div className="space-y-3" data-testid="video-preview-block">
          {preview && (
            <video
              src={preview}
              className="w-full max-h-60 bg-black border border-white/10"
              controls
              muted
              data-testid="video-preview"
            />
          )}
          <p className="font-body text-[10px] tracking-wide text-white/55 truncate">{file.name} - {(file.size / (1024 * 1024)).toFixed(1)} MB</p>
          {!analysis && (
            <button
              onClick={submit}
              disabled={busy}
              className="btn-primary-cta w-full text-sm"
              data-testid="video-analyze-btn"
            >
              {busy ? (
                <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Analyzing</span>
              ) : (
                <span className="inline-flex items-center gap-2"><Camera className="h-4 w-4" strokeWidth={2} /> Analyze With Gemini</span>
              )}
            </button>
          )}
        </div>
      )}

      {err && (
        <div className="mt-3 border border-red-500/40 bg-red-500/10 p-3 font-body text-xs text-red-300" data-testid="video-error">
          {err}
        </div>
      )}

      {a && (
        <div className="mt-4 space-y-3" data-testid="video-analysis-result">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-plai-teal" strokeWidth={1.5} />
            <span className="font-body text-[10px] tracking-[0.2em] uppercase text-plai-teal">Evidence Exhibit Ready</span>
            <span className="font-body text-[10px] text-white/40 ml-auto">Confidence {(a.confidence_score * 100).toFixed(0)}%</span>
          </div>
          <p className="font-body text-sm text-white/85 leading-relaxed bg-black/40 p-3 border-l-2 border-plai-purple" data-testid="video-exhibit-paragraph">
            {a.evidence_exhibit_paragraph}
          </p>

          <details className="font-body text-xs text-white/65" data-testid="video-details">
            <summary className="cursor-pointer text-white/80 uppercase tracking-[0.2em] text-[10px]">Full Findings</summary>
            <div className="mt-2 space-y-2 pl-3 border-l border-white/10">
              {a.machinery_observed?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Machinery</p>
                  {a.machinery_observed.map((m, i) => (
                    <p key={i}>- {m.type}{m.count_estimate ? ` (${m.count_estimate})` : ''}{m.condition ? ` - ${m.condition}` : ''}</p>
                  ))}
                </div>
              )}
              {a.fencing_observed?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Fencing</p>
                  {a.fencing_observed.map((m, i) => (
                    <p key={i}>- {m.type}{m.condition ? ` - ${m.condition}` : ''}{m.implication ? ` (${m.implication})` : ''}</p>
                  ))}
                </div>
              )}
              {a.land_features?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Land Features</p>
                  {a.land_features.map((m, i) => <p key={i}>- {m}</p>)}
                </div>
              )}
              {a.wildlife_or_habitat_signals?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Wildlife / Habitat</p>
                  {a.wildlife_or_habitat_signals.map((m, i) => <p key={i}>- {m}</p>)}
                </div>
              )}
              {a.dark_sky_or_lighting_concerns && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Dark Sky / Lighting</p>
                  <p>- {a.dark_sky_or_lighting_concerns}</p>
                </div>
              )}
              {a.soil_or_water_disturbance && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Soil / Water</p>
                  <p>- {a.soil_or_water_disturbance}</p>
                </div>
              )}
            </div>
          </details>
          <p className="font-body text-[10px] tracking-wide text-white/40 mt-2">
            This Evidence Exhibit is a separate, verified attachment. The 6-Agent War Room is not modified.
          </p>
        </div>
      )}
    </div>
  );
}
